// The combined session-list read — the composition over the v1 / v2 / v3 list
// queries, plus the offline-recording union it layers on top.
//
// A CORE SPLIT, not a whole-file move: `src/hooks/query/useQueryCombinedSessions.ts`
// keeps its module identity and is now a delegate over this. The four
// version-dispatched cache commands it re-exports never lived in it — they
// moved with `sessionsListCache.ts`. History for the moved bodies:
// `git log --follow src/hooks/query/useQueryCombinedSessions.ts`.
//
// ### The three legs are INJECTED HOOKS, and that is the one thing to read twice
//
// `useQuerySessions` (v1), `useQuerySessionsV2` (v2) and `useQueryAdminSessions`
// (v3) are app-side. The v1/v2 pair are delegates whose own cores are already
// here, but what is left of them is the REVIEW-CONTEXT COMPOSITION — six app
// inputs deciding whether this user may read another member's list — and the v3
// query has not moved at all (its filter/sort/search atoms are the session-list
// toolbar's). So this hook takes the three as hook-valued parameters and calls
// each unconditionally, in the legacy order.
//
// This is deliberately NOT a config-seam member. The seam is for values that
// are app-shaped by nature; these three are session-domain reads that are
// merely not carved YET, and the parameters are how that shows in the type. It
// heals by deletion: when the review-context composition and the v3 filters
// have their own homes here, the parameters go and this hook calls them
// directly.
//
// ### What crosses the seam instead
//
// The cohort/flag/desktop trio and the two offline-recording app atoms cross as
// ONE required `SessionDataConfig.useSessionListEnvironment` member — ruling 32's
// shape, read once and unconditionally. `sessionQueryVersionAtom` crosses as
// NOTHING: it is package-owned (`../atoms`), and `src/atoms/sessionAtoms.tsx`
// is a re-export of it.

import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { BaseStatus, SessionStatus } from '@heidi/schema/ml-scribe';

import {
  backfillQueueServerSessionId,
  orderOfflineRowsByCreatedAt,
  type OfflineLocalSessionListItem,
  type OfflineSyncItem,
  type OfflineSyncStatus,
  type SimpleSessionResponse,
} from '../../contracts';
import { sessionQueryVersionAtom } from '../atoms';
import { getSessionDataConfig } from '../config';

type SessionLike = SimpleSessionResponse;

type OfflineSuppressibleQuery = {
  data?: unknown;
  error: unknown;
  fetchStatus?: 'fetching' | 'paused' | 'idle';
  failureCount: number;
  failureReason: unknown;
  isError: boolean;
  isFetching?: boolean;
  isLoading: boolean;
  isLoadingError: boolean;
  isPending: boolean;
  isRefetching?: boolean;
  isRefetchError: boolean;
  isSuccess: boolean;
  status: 'pending' | 'error' | 'success';
};

const suppressOfflineListError = <T extends OfflineSuppressibleQuery>(
  query: T,
  isDesktopOnlineReady: boolean
): T => {
  const isLoadingOrFetching =
    query.isLoading ||
    query.isFetching ||
    query.isRefetching ||
    query.fetchStatus === 'fetching';
  if (isDesktopOnlineReady || (!query.isError && !isLoadingOrFetching)) return query;
  return {
    ...query,
    error: null,
    fetchStatus: 'idle',
    failureCount: 0,
    failureReason: null,
    isError: false,
    isFetching: false,
    isLoading: false,
    isLoadingError: false,
    isPending: false,
    isRefetching: false,
    isRefetchError: false,
    isSuccess: true,
    status: 'success',
  };
};

const resolveOfflineSessionId = (item: OfflineSyncItem): string =>
  item.serverSessionId ?? item.localSessionId;

type OfflineSessionListUnionItem = {
  localSessionId: string;
  serverSessionId: string | null;
  createdAt: string;
  updatedAt: string;
  status?: OfflineSyncStatus;
  userId?: string;
};

const buildOfflineSessionListItem = (
  item: OfflineSessionListUnionItem,
  sessionId: string
): SessionLike => {
  return {
    session_id: sessionId,
    kinde_gp_id: item.userId ?? '',
    created_at: item.createdAt,
    created_time_local: item.createdAt,
    updated_at: item.updatedAt,
    patient: {
      demographic_details: '',
      name: '',
    },
    patient_profile: null,
    session_name: null,
    session_gist: null,
    session_status: SessionStatus.EMPTY,
    status: BaseStatus.CREATED,
    duration: 0,
    is_consent: false,
    offline_sync_status: item.status,
    offline_local_session_id: item.localSessionId,
    is_offline_synthetic: true,
  } as SessionLike;
};

const unionOfflineSessions = (
  sessions: SessionLike[] | undefined,
  localSessions: OfflineLocalSessionListItem[],
  queue: OfflineSyncItem[],
  isOfflineRecordingEnabled: boolean
): SessionLike[] | undefined => {
  if (!isOfflineRecordingEnabled) return sessions;
  if (localSessions.length === 0 && queue.length === 0) return sessions;

  const baseSessions = sessions ?? [];
  const localSessionByLocalId = new Map(
    localSessions.map((item) => [item.localSessionId, item])
  );
  const queueBySessionId = new Map<string, OfflineSyncItem>();
  for (const item of queue) {
    const queueItem = backfillQueueServerSessionId(item, localSessionByLocalId);
    queueBySessionId.set(resolveOfflineSessionId(queueItem), queueItem);
  }
  const patchedSessions = baseSessions.map((session) => {
    const queueItem = session.session_id
      ? queueBySessionId.get(session.session_id)
      : undefined;
    if (!queueItem) return session;

    return {
      ...session,
      offline_sync_status: queueItem.status,
      offline_local_session_id: queueItem.localSessionId,
    };
  });
  const existingIds = new Set(
    patchedSessions
      .map((session) => session.session_id)
      .filter((sessionId): sessionId is string => Boolean(sessionId))
  );
  const queuedLocalSessionIds = new Set(queue.map((item) => item.localSessionId));

  // Collect first, order by datetime, THEN build the rows. Both loops below
  // walk de-duping maps/atoms in hydration order, which is not datetime order —
  // prepending them raw is what left an offline list unsorted.
  const syntheticItems: (OfflineSessionListUnionItem & { sessionId: string })[] = [];
  for (const [sessionId, item] of queueBySessionId) {
    if (existingIds.has(sessionId)) continue;
    syntheticItems.push({ ...item, sessionId });
  }

  for (const localSession of localSessions) {
    if (
      queuedLocalSessionIds.has(localSession.localSessionId) ||
      existingIds.has(localSession.serverSessionId ?? localSession.localSessionId)
    ) {
      continue;
    }
    syntheticItems.push({
      ...localSession,
      sessionId: localSession.serverSessionId ?? localSession.localSessionId,
    });
  }

  // Hardcoded `'desc'` on purpose — do NOT add a direction parameter here.
  // Unlike the new list's `unionOfflineSyncQueue`, no caller of this legacy
  // composition could supply one, and no caller would observe it:
  //   - The one direction control that reaches this data — the legacy sidebar
  //     toolbar (`sessionsSortByAtom` / `sessionsSortOrderAtom`) — is applied
  //     DOWNSTREAM in `useSessionsFilteredList`, which re-sorts the whole
  //     unioned list (synthetic + backend rows together) by
  //     `created_time_local`. Synthetic rows carry `created_time_local =
  //     createdAt` (above), so they already land in the right place in both
  //     directions and this order is discarded before render.
  //   - The v3 leg is the only one that pushes that sort server-side, and it is
  //     unreachable: `sessionQueryVersionAtom` defaults to 'v2' and its only two
  //     setters write 'v1'.
  //   - The consolidated list reaches this hook through `useSessionSearch` and
  //     DOES have a sort control, but it re-sorts the whole combined list by its
  //     own toolbar state after unioning, so a direction supplied here would be
  //     overwritten before render. It also runs a second union of its own for
  //     the rows this one did not produce; that union orders with the same
  //     shared comparator, so the two cannot drift.
  //   - The remaining consumers (side-nav list, transcription picker,
  //     appointment list, eligibility checks) have no sort UI at all, so
  //     newest-first is the right fixed default for them.
  // This ordering therefore exists so the no-sort consumers stop rendering
  // hydration order, and so the sorted consumers get a deterministic block to
  // re-sort rather than an arbitrary one.
  const synthetic = orderOfflineRowsByCreatedAt(syntheticItems).map((item) =>
    buildOfflineSessionListItem(item, item.sessionId)
  );

  return [...synthetic, ...patchedSessions];
};

/** The v1 leg — a flat list. */
export type CombinedSessionsFlatLeg = OfflineSuppressibleQuery & {
  data?: { sessions?: SessionLike[] } | undefined;
};

/**
 * The v2 leg — infinite pages, and the shape the v1 branch's no-op copies.
 *
 * `sessions` is optional here on purpose. The contract makes it required and
 * the backend model does too, but a page only has to REACH the cache to be
 * read from — a 2xx whose body is not the JSON we expect lands as a page
 * without one (axios parses silently and hands back the raw value).
 */
export type CombinedSessionsInfiniteLeg = OfflineSuppressibleQuery & {
  data?: { pages: { sessions?: SessionLike[] }[] } | undefined;
  fetchNextPage: (...args: never[]) => unknown;
};

/** The v3 leg — infinite pages that also carry the whole-scope match count. */
export type CombinedSessionsSearchLeg = OfflineSuppressibleQuery & {
  data?:
    | { pages: { sessions?: SessionLike[]; total_match_count?: number }[] }
    | undefined;
};

/**
 * Combined hook that abstracts the three session query hooks (V1, V2, V3)
 * and returns a normalized data structure with consistent query state.
 *
 * Returns the active query result based on feature flags, with:
 * - `sessions`: Normalized sessions array (flattened from pages for V2/V3)
 * - All standard TanStack Query properties from the active query
 * - Infinite query methods (`fetchNextPage`, `hasNextPage`, `isFetchingNextPage`)
 *
 * ⚠️ This is the LEGACY sessions query (`GET /api/v2/ml-scribe/sessions`). The
 * new session list is served exclusively by the v2.2 endpoint
 * (`useQuerySessionsList` → `GET /api/v2-2/ml-scribe/sessions`), so this query
 * defaults OFF while the new list is on — gated here at the single chokepoint
 * rather than per caller (React Query dedupes by key, so any one mounted
 * subscriber with `enabled: true` re-fires it for everyone). A caller that
 * genuinely needs this legacy list on the new list must opt back in explicitly
 * with `{ enabled: true }` (see the EHR appointment↔session map); everyone else
 * omits `enabled` and inherits the gate.
 */
export function useQueryCombinedSessions<
  TFlatLeg extends CombinedSessionsFlatLeg,
  TInfiniteLeg extends CombinedSessionsInfiniteLeg,
  TSearchLeg extends CombinedSessionsSearchLeg,
>({
  enabled,
  suppressOfflineSyntheticSessions = false,
  useSessionsV1,
  useSessionsV2,
  useSessionsV3,
}: {
  enabled?: boolean;
  suppressOfflineSyntheticSessions?: boolean;
  /** `useQuerySessions` — the v1 flat list, review context resolved app-side. */
  useSessionsV1: () => TFlatLeg;
  /** `useQuerySessionsV2` — takes the resolved chokepoint gate. */
  useSessionsV2: (enabled: boolean) => TInfiniteLeg;
  /** `useQueryAdminSessions` — the v3 paged search over the toolbar filters. */
  useSessionsV3: () => TSearchLeg;
}) {
  const {
    isNewSessionListEnabled,
    isOfflineRecordingEnabled,
    isDesktopOnlineReady,
    offlineLocalSessions,
    offlineSyncQueue,
  } = getSessionDataConfig().useSessionListEnvironment();
  // Omitted `enabled` → gated by the flag (off on the new list). An explicit
  // `true`/`false` from the caller always wins.
  const effectiveEnabled = enabled ?? !isNewSessionListEnabled;
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const queryV1 = useSessionsV1();
  // The gate only bites the v2 path: with the new session list flag on, the
  // query version is always 'v2' (the legacy toolbar/search that flips it to
  // 'v1' isn't mounted there, and nothing sets 'v3'), so v2 is the only path
  // that would otherwise fire the legacy `/sessions` request.
  const queryV2 = useSessionsV2(effectiveEnabled);
  const queryV3 = useSessionsV3();

  // Normalize sessions data from the active query.
  //
  // `page.sessions ?? []` is load-bearing, not defensive noise: `flatMap`
  // flattens an array and appends anything else as a single element, so a page
  // that reaches the cache without a `sessions` array puts a literal
  // `undefined` ROW into this list. Every consumer then holds a collection it
  // cannot type-check its way out of — the sidebar's Fuse index reads
  // `session.patient` through a custom `getFn` and takes the whole session page
  // down with it (SCR-7433).
  const onlineSessions = useMemo(() => {
    switch (sessionQueryVersion) {
      case 'v3':
        return queryV3.data?.pages.flatMap((page) => page.sessions ?? []);
      case 'v2':
        return queryV2.data?.pages.flatMap((page) => page.sessions ?? []);
      default:
        return queryV1.data?.sessions;
    }
  }, [
    sessionQueryVersion,
    queryV1.data?.sessions,
    queryV3.data?.pages,
    queryV2.data?.pages,
  ]);
  const sessions = useMemo(
    () =>
      unionOfflineSessions(
        onlineSessions,
        offlineLocalSessions,
        offlineSyncQueue,
        isOfflineRecordingEnabled && !suppressOfflineSyntheticSessions
      ),
    [
      onlineSessions,
      offlineLocalSessions,
      offlineSyncQueue,
      isOfflineRecordingEnabled,
      suppressOfflineSyntheticSessions,
    ]
  );

  // Determine which query is active and return its result
  switch (sessionQueryVersion) {
    case 'v3':
      return {
        ...suppressOfflineListError(queryV3, isDesktopOnlineReady),
        sessions,
        totalMatchCount: queryV3.data?.pages.at(0)?.total_match_count,
      };
    case 'v2':
      return {
        ...suppressOfflineListError(queryV2, isDesktopOnlineReady),
        sessions,
        totalMatchCount: undefined as number | undefined,
      };
    default:
      // V1 doesn't have infinite query methods, provide no-op/default values
      return {
        ...suppressOfflineListError(queryV1, isDesktopOnlineReady),
        sessions,
        totalMatchCount: undefined as number | undefined,
        fetchNextPage: () =>
          Promise.resolve({} as ReturnType<TInfiniteLeg['fetchNextPage']>),
        hasNextPage: false,
        isFetchingNextPage: false,
      };
  }
}
